import { Reveal } from "@/components/motion/Reveal";
import { ASSETS, LINKS } from "@/lib/site";
import { FileText, Clock, Calculator, CheckCircle2, ArrowUpRight } from "lucide-react";

const VOTE_FEATURES = [
  { icon: Clock, title: "Minutes, not months", body: "Answer a guided set of prompts and get a structured proposal instantly." },
  { icon: Calculator, title: "Costed & defensible", body: "Line-item budgets your council can bring straight to the table." },
  { icon: FileText, title: "Ready to share", body: "Export a polished document that reads like your nation wrote it." },
  { icon: CheckCircle2, title: "Public & accessible", body: "Open to any Indigenous community member helping plan a voting event." },
];

export default function Products() {
  return (
    <>
      {/* Vote-Estimate — bright, public, accessible */}
      <section id="vote-estimate" className="py-24 md:py-36" data-testid="vote-estimate">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center lg:items-start">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.28em] font-semibold text-primary mb-6">
                Public tool · Open to all
              </p>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[0.95]">
                Vote-Estimate
              </h2>
              <p className="mt-6 text-lg text-foreground/70 leading-relaxed max-w-lg">
                Turn the daunting task of planning a voting event into a clear,
                costed proposal — in a single sitting. Guided, friendly, and made
                for the people doing the work on the ground.
              </p>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {VOTE_FEATURES.map((f, i) => (
                  <Reveal key={f.title} delay={i * 0.06}>
                    <div className="rounded-lg border border-border bg-card p-5 h-full hover:border-primary/60 hover:-translate-y-1 transition-[transform,border-color] duration-300">
                      <f.icon className="text-primary mb-3" size={22} />
                      <h3 className="font-medium mb-1.5">{f.title}</h3>
                      <p className="text-sm text-foreground/60 leading-relaxed">{f.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <a
                href={LINKS.voteEstimate}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="vote-estimate-cta"
                className="group inline-flex items-center gap-2 mt-10 px-7 py-3.5 rounded-md bg-primary text-primary-foreground font-medium hover:brightness-95 transition-[filter] duration-300"
              >
                Start a proposal
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -inset-4 rounded-2xl bg-primary/10 -z-10 blur-2xl" />
                <div className="rounded-xl overflow-hidden border border-border shadow-[0_30px_80px_-40px_rgba(18,24,21,0.4)]">
                  <img src={ASSETS.costShot} alt="Vote-Estimate live cost estimate — total budget breakdown" className="w-full" />
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 md:mt-24">
              <p className="text-xs uppercase tracking-[0.28em] font-semibold text-primary mb-6">
                From event details to a client-ready PDF
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <figure className="rounded-xl overflow-hidden border border-border shadow-[0_30px_80px_-40px_rgba(26,17,24,0.35)]">
                  <img src={ASSETS.recipientShot} alt="Recipient information step in Vote-Estimate" className="w-full" />
                </figure>
                <figure className="rounded-xl overflow-hidden border border-border shadow-[0_30px_80px_-40px_rgba(26,17,24,0.35)]">
                  <img src={ASSETS.proposalShot} alt="Delivered, brand-consistent proposal PDF" className="w-full" />
                </figure>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
